import { TABLE_SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { PredictiveAudienceGoal, PredictiveGoalsTableColValues } from './types';

const NA = 'n/a';

const getFormattedValueByKey = (
  sortKey: keyof typeof PredictiveGoalsTableColValues,
  formatter: typeof PredictiveGoalsTableFormatter,
  val1: PredictiveAudienceGoal,
  val2: PredictiveAudienceGoal,
) => {
  return formatter[sortKey](val1, val2);
};

const PredictiveGoalsTableFormatter = {
  [PredictiveGoalsTableColValues.name]: (
    val1: PredictiveAudienceGoal,
    val2: PredictiveAudienceGoal,
  ) => {
    return {
      formattedValue1: val1.name?.toLowerCase() || NA,
      formattedValue2: val2.name?.toLowerCase() || NA,
    };
  },
  [PredictiveGoalsTableColValues.status]: (
    val1: PredictiveAudienceGoal,
    val2: PredictiveAudienceGoal,
  ) => {
    return {
      formattedValue1: val1.status?.toLowerCase() || NA,
      formattedValue2: val2.status?.toLowerCase() || NA,
    };
  },
  [PredictiveGoalsTableColValues.userProfileField]: (
    val1: PredictiveAudienceGoal,
    val2: PredictiveAudienceGoal,
  ) => {
    return {
      formattedValue1: val1.userProfileField?.toLowerCase() || NA,
      formattedValue2: val2.userProfileField?.toLowerCase() || NA,
    };
  },
  [PredictiveGoalsTableColValues.updatedAt]: (
    val1: PredictiveAudienceGoal,
    val2: PredictiveAudienceGoal,
  ) => {
    return {
      formattedValue1: val1.updatedAt?.timestamp?.toLowerCase() || NA,
      formattedValue2: val2.updatedAt?.timestamp?.toLowerCase() || NA,
    };
  },
  [PredictiveGoalsTableColValues.lastTrainedAt]: (
    val1: PredictiveAudienceGoal,
    val2: PredictiveAudienceGoal,
  ) => {
    return {
      formattedValue1: val1.lastTrainedAt?.timestamp?.toLowerCase() || NA,
      formattedValue2: val2.lastTrainedAt?.timestamp?.toLowerCase() || NA,
    };
  },
  [PredictiveGoalsTableColValues.modelDetails]: (
    val1: PredictiveAudienceGoal,
    val2: PredictiveAudienceGoal,
  ) => {
    return {
      formattedValue1: val1.modelDetails?.model_quality?.toLowerCase() || NA,
      formattedValue2: val2.modelDetails?.model_quality?.toLowerCase() || NA,
    };
  },
};

const sortCallback = (
  sortKey: keyof typeof PredictiveGoalsTableColValues,
  sortDirection: string,
) => {
  return (val1: PredictiveAudienceGoal, val2: PredictiveAudienceGoal) => {
    const { formattedValue1, formattedValue2 } = getFormattedValueByKey(
      sortKey,
      PredictiveGoalsTableFormatter,
      val1,
      val2,
    );
    if (!formattedValue1 || !formattedValue2) {
      return 0;
    }

    if (sortDirection === TABLE_SORT_DIRECTIONS.DESCENDING) {
      return formattedValue1 > formattedValue2 ? -1 : 1;
    }
    return formattedValue2 > formattedValue1 ? -1 : 1;
  };
};

/**
 * Sort objects by a value and direction
 * @param {[{PredictiveAudienceGoal}]} objectArray - an array of PredictiveAudienceGoals
 * @param {string} sortKey  - value set in the column header
 * @param {string} sortDirection - ASCENDING or DESCENDING, DESCENDING is default
 */
export const customPGSortByObjValue = (
  objectArray: PredictiveAudienceGoal[] = [],
  sortKey: keyof typeof PredictiveGoalsTableColValues,
  sortDirection: string = TABLE_SORT_DIRECTIONS.DESCENDING,
) => objectArray.sort(sortCallback(sortKey, sortDirection));
